import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../components/ui/Button';
import ErrorDialog from '../components/ui/ErrorDialog';
import Label from '../components/ui/Label';
import Textarea from '../components/ui/Textarea';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import { HEADER_SELECT_CLASS, PageHeaderActions } from '../components/PageHeaderActions';
import CriteriaTree from '../components/segmentBuilder/CriteriaTree';
import SegmentSummaryPane from '../components/segmentBuilder/SegmentSummaryPane';
import NlDraftBar from '../components/segmentBuilder/NlDraftBar';
import { segmentTemplatesService } from '../services/segmentTemplatesService';
import {
  collectConditions,
  criteriaFromSegment,
  formatApiErrorDetail,
  hydrateCriteria,
  sanitizeName,
} from '../components/segmentBuilder/criteriaEditorHelpers';
import {
  contradictionIssues,
  criteriaIncompleteReason,
  criteriaIsComplete,
  defaultCriteriaRoot,
  fitToTarget,
  hoistSampleToAncestor,
  mergeSameColumnAtPath,
  pathKey,
  samplingShapeIssues,
  setOpAtPath,
  anyGroupSampled,
} from '../components/segmentBuilder/criteriaUtils';
import { api } from '../services/authService';
import { organizationsService } from '../services/organizationsService';
import { wakeSqlWarehouse } from '../services/sqlWarehouseService';
import { SEGMENT_TEMPLATES_LIST_PAGE_KEY, parseListPage, readStoredListPage } from '../utils/listPage';
import SaveStatus from '../components/segmentBuilder/SaveStatus';
import '../components/segmentBuilder/CriteriaBuilder.css';

const PREVIEW_DEBOUNCE_MS = 450;
const SAVE_DEBOUNCE_MS = 2500;

/** Back-link target: location.state first, then the last Templates-tab page in this tab. */
function templatesListPath(location) {
  const fromState = parseListPage(location?.state?.templateListPage);
  const page = fromState > 1 ? fromState : readStoredListPage(SEGMENT_TEMPLATES_LIST_PAGE_KEY);
  const params = new URLSearchParams();
  params.set('tab', 'templates');
  if (page > 1) params.set('tpage', String(page));
  return `/segments?${params.toString()}`;
}

const TemplateEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const seedFromId = isNew ? searchParams.get('from') : null;
  const templatesPath = templatesListPath(location);

  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState('');
  const [template, setTemplate] = useState(null); // saved server copy
  const [loading, setLoading] = useState(!isNew || Boolean(seedFromId));
  const [loadError, setLoadError] = useState('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [criteria, setCriteria] = useState(defaultCriteriaRoot());
  const [targetSize, setTargetSize] = useState('');

  const [sql, setSql] = useState('');
  const [sqlError, setSqlError] = useState('');
  const [explain, setExplain] = useState(null);

  const [count, setCount] = useState(null);
  const [countStatus, setCountStatus] = useState('idle'); // idle | running | fresh | stale | error
  const [countTookMs, setCountTookMs] = useState(null);
  const [countError, setCountError] = useState('');
  const [autoCount, setAutoCount] = useState(true);
  const [groupCounts, setGroupCounts] = useState(null);

  const [analyzeStatus, setAnalyzeStatus] = useState('idle');
  const [analyzeResults, setAnalyzeResults] = useState(null);
  const [analyzeError, setAnalyzeError] = useState('');

  const [saveStatus, setSaveStatus] = useState('saved');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const criteriaJson = useMemo(() => JSON.stringify(criteria), [criteria]);
  const complete = useMemo(() => criteriaIsComplete(criteria), [criteria]);
  const previewReq = useRef(0);
  const countReq = useRef(0);
  const analyzeReq = useRef(0);
  const countedFor = useRef('');
  const savedSnapshot = useRef('');
  const lastSqlFor = useRef('');
  const latestCriteriaJson = useRef('');
  latestCriteriaJson.current = criteriaJson;
  const saveInFlight = useRef(false);

  const sortedOrganizations = useMemo(
    () => [...organizations].sort((a, b) => a.name.localeCompare(b.name)),
    [organizations]
  );
  const organizationName = useMemo(
    () => organizations.find((o) => String(o.id) === String(organizationId))?.name || template?.organization_name || '',
    [organizations, organizationId, template]
  );
  // A saved, org-owned template keeps its organization fixed; only a new
  // template (or a legacy, org-independent one) lets you pick one.
  const orgIsFixed = !isNew && Boolean(template?.organization_id);

  // ---- load orgs + template -------------------------------------------------
  useEffect(() => {
    wakeSqlWarehouse();
    let cancelled = false;
    let loadedOrgs = [];
    const defaultOrgPick = (orgs) => {
      const saved = organizationsService.getSelectedOrganization('id');
      return saved && orgs.some((o) => String(o.id) === String(saved)) ? String(saved) : orgs[0] ? String(orgs[0].id) : '';
    };
    (async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        if (cancelled) return;
        loadedOrgs = orgs;
        setOrganizations(orgs);
        if (isNew) setOrganizationId(defaultOrgPick(orgs));
      } catch (e) {
        if (!cancelled) setLoadError('Failed to load organizations.');
      }
      if (isNew) {
        if (seedFromId) {
          try {
            const t = await segmentTemplatesService.get(seedFromId);
            if (cancelled) return;
            const hydrated = await hydrateCriteria(
              criteriaFromSegment(t.criteria),
              t.organization_id || defaultOrgPick(loadedOrgs)
            );
            if (cancelled) return;
            if (t.organization_id) setOrganizationId(String(t.organization_id));
            setName(sanitizeName(`Copy of ${t.name || ''}`.trim()));
            setDescription(t.description || '');
            setCriteria(hydrated);
          } catch (e) {
            if (!cancelled) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || 'Could not load the template to copy');
            }
          } finally {
            if (!cancelled) setLoading(false);
          }
          return;
        }
        setLoading(false);
        return;
      }
      try {
        const t = await segmentTemplatesService.get(id);
        if (cancelled) return;
        const hydrated = await hydrateCriteria(
          criteriaFromSegment(t.criteria),
          t.organization_id || defaultOrgPick(loadedOrgs)
        );
        if (cancelled) return;
        setTemplate(t);
        // A legacy (pre-org-ownership) template has no fixed org; default one
        // just so a count can be run, without changing what gets saved.
        setOrganizationId(t.organization_id ? String(t.organization_id) : defaultOrgPick(loadedOrgs));
        setName(t.name || '');
        setDescription(t.description || '');
        setCriteria(hydrated);
        setCount(t.md5_count ?? null);
        setCountStatus(t.md5_count != null ? 'fresh' : 'idle');
        const cj = JSON.stringify(hydrated);
        countedFor.current = t.md5_count != null ? cj : '';
        savedSnapshot.current = JSON.stringify({
          name: t.name || '',
          description: t.description || '',
          cj,
        });
        setSaveStatus('saved');
      } catch (e) {
        if (!cancelled) setLoadError(formatApiErrorDetail(e.response?.data?.detail) || 'Template not found.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, seedFromId]);

  // ---- preview SQL (debounced) --------------------------------------------
  useEffect(() => {
    if (!organizationId) return undefined;
    if (!complete) {
      previewReq.current += 1;
      setExplain(null);
      setSqlError('');
      return undefined;
    }
    if (criteriaJson === lastSqlFor.current) return undefined;
    const t = setTimeout(async () => {
      const req = ++previewReq.current;
      try {
        const res = await api.post('/api/v2/segments/preview-sql', { criteria, organization_id: parseInt(organizationId, 10) });
        if (req !== previewReq.current) return;
        setSql(res.data?.sql || '');
        setExplain(res.data?.explain || null);
        setSqlError('');
        lastSqlFor.current = criteriaJson;
      } catch (e) {
        if (req !== previewReq.current) return;
        setSqlError(formatApiErrorDetail(e.response?.data?.detail) || 'Could not generate SQL.');
        setExplain(null);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteriaJson, complete, organizationId]);

  useEffect(() => {
    if (criteriaJson === countedFor.current) return;
    setCountError('');
    if (count != null) setCountStatus((s) => (s === 'running' ? s : 'stale'));
  }, [criteriaJson, count]);

  // ---- count --------------------------------------------------------------
  const runCount = useCallback(async () => {
    if (!organizationId || !criteriaIsComplete(criteria)) return null;
    const cj = JSON.stringify(criteria);
    const req = ++countReq.current;
    setCountStatus('running');
    setCountError('');
    try {
      const res = await api.post('/api/v2/segments/run-count', {
        criteria,
        organization_id: parseInt(organizationId, 10),
        include_group_counts: true,
      });
      if (req !== countReq.current) return null;
      const n = Number(res.data?.count);
      const resolved = Number.isFinite(n) ? n : null;
      setCount(resolved);
      setCountTookMs(Number(res.data?.took_ms) || null);
      const gc = res.data?.group_counts;
      setGroupCounts(gc && typeof gc === 'object' && Object.keys(gc).length ? gc : null);
      if (res.data?.sql) setSql(res.data.sql);
      countedFor.current = cj;
      setCountStatus(latestCriteriaJson.current === cj ? 'fresh' : 'stale');
      return resolved;
    } catch (e) {
      if (req !== countReq.current) return null;
      setCountStatus('error');
      setCountError(formatApiErrorDetail(e.response?.data?.detail) || 'Count failed.');
      return null;
    }
  }, [criteria, organizationId]);

  useEffect(() => {
    if (!autoCount || !complete || !organizationId) return undefined;
    if (criteriaJson === countedFor.current) return undefined;
    const t = setTimeout(() => runCount(), 1500);
    return () => clearTimeout(t);
  }, [criteriaJson, complete, autoCount, organizationId, runCount]);

  // ---- analyze (leave-one-out condition impact) ----------------------------
  useEffect(() => {
    setAnalyzeStatus('idle');
    setAnalyzeResults(null);
    setAnalyzeError('');
  }, [criteriaJson]);

  const runAnalyze = useCallback(async () => {
    if (!organizationId || !criteriaIsComplete(criteria)) return;
    const req = ++analyzeReq.current;
    setAnalyzeStatus('running');
    setAnalyzeError('');
    try {
      const res = await api.post('/api/v2/segments/analyze-conditions', { criteria, organization_id: parseInt(organizationId, 10) });
      if (req !== analyzeReq.current) return;
      setAnalyzeResults({ baseCount: Number(res.data?.base_count) || 0, conditions: Array.isArray(res.data?.conditions) ? res.data.conditions : [] });
      setAnalyzeStatus('done');
    } catch (e) {
      if (req !== analyzeReq.current) return;
      setAnalyzeStatus('error');
      setAnalyzeError(formatApiErrorDetail(e.response?.data?.detail) || 'Analysis failed.');
    }
  }, [criteria, organizationId]);

  // ---- target audience size -----------------------------------------------
  const samplingActive = useMemo(() => anyGroupSampled(criteria), [criteria]);
  const matchingTotal = groupCounts ? groupCounts[pathKey([])] : null;
  const countAfterFit = useRef(false);

  const fitTargetSize = () => {
    const target = Number(String(targetSize).replace(/[^\d.]/g, ''));
    const result = fitToTarget(criteria, { groupCounts: groupCounts || {}, currentTotal: count, target });
    if (!result.ok) {
      toast.error(result.reason);
      return;
    }
    if (result.reason) toast.success(result.reason);
    setCriteria(result.criteria);
    countAfterFit.current = true;
  };

  useEffect(() => {
    if (!countAfterFit.current || !complete || !organizationId) return;
    countAfterFit.current = false;
    runCount();
  }, [criteriaJson, complete, organizationId, runCount]);

  const shapeIssues = useMemo(() => samplingShapeIssues(criteria), [criteria]);

  const hoistStrandedSamples = () => {
    setCriteria((prev) => {
      let next = prev;
      for (let pass = 0; pass < 8; pass += 1) {
        const issues = samplingShapeIssues(next);
        if (!issues.length) break;
        next = issues.reduce((acc, i) => hoistSampleToAncestor(acc, i.path, i.ancestorPath), next);
      }
      return next;
    });
    toast.success('Percentage moved up to the level above — the same profiles are still selected.');
    countAfterFit.current = true;
  };

  const loosenStrandedLevels = () => {
    setCriteria((prev) => {
      const levels = new Map(samplingShapeIssues(prev).map((i) => [pathKey(i.ancestorPath), i.ancestorPath]));
      return [...levels.values()].reduce((acc, p) => setOpAtPath(acc, p, 'OR'), prev);
    });
    toast.success('Switched to "Any of" — each group now adds its own slice, so the audience grows.');
    countAfterFit.current = true;
  };

  const contradictions = useMemo(() => contradictionIssues(criteria), [criteria]);

  const combineContradictions = () => {
    setCriteria((prev) =>
      contradictionIssues(prev)
        .filter((i) => i.canMerge)
        .reverse()
        .reduce((acc, i) => mergeSameColumnAtPath(acc, i.path, i.columnKey), prev)
    );
    toast.success('Combined into "is any of" — profiles matching any of those values now qualify.');
  };

  // ---- save -----------------------------------------------------------------
  const currentSnapshot = useMemo(
    () => JSON.stringify({ name: name.trim(), description: description.trim(), cj: criteriaJson }),
    [name, description, criteriaJson]
  );
  const dirty = !isNew && currentSnapshot !== savedSnapshot.current;
  const saveable = Boolean(name.trim()) && complete && Boolean(organizationId);
  const notSaveableReason = !name.trim()
    ? 'Name the template to save'
    : !complete
      ? criteriaIncompleteReason(criteria)
      : !organizationId
        ? 'Select an organization'
        : '';

  const createBlockers = () => {
    const blockers = [];
    if (!name.trim()) blockers.push('Give the template a name.');
    if (!complete) blockers.push(criteriaIncompleteReason(criteria));
    if (!organizationId) blockers.push('Select an organization.');
    return blockers.filter(Boolean);
  };

  const buildPayload = () => ({
    name: name.trim(),
    description: description.trim() || null,
    criteria,
    md5_count: countedFor.current === criteriaJson ? count : null,
  });

  const saveExisting = useCallback(async () => {
    if (isNew || !template || saveInFlight.current) return false;
    const snap = currentSnapshot;
    saveInFlight.current = true;
    setSaveStatus('saving');
    try {
      const updated = await segmentTemplatesService.update(template.id, buildPayload());
      setTemplate(updated);
      savedSnapshot.current = snap;
      setSaveStatus('saved');
      return true;
    } catch (e) {
      setSaveStatus('error');
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to save template');
      return false;
    } finally {
      saveInFlight.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, template, currentSnapshot, criteria, count, name, description]);

  useEffect(() => {
    if (isNew || !template) return undefined;
    if (!dirty) {
      setSaveStatus((s) => (s === 'saving' ? s : 'saved'));
      return undefined;
    }
    if (!saveable) {
      setSaveStatus('invalid');
      return undefined;
    }
    setSaveStatus('dirty');
    const t = setTimeout(() => saveExisting(), SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [dirty, saveable, isNew, template, saveExisting, currentSnapshot]);

  const pendingSaveRef = useRef(null);
  pendingSaveRef.current = dirty && saveable && !saveInFlight.current ? saveExisting : null;
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (pendingSaveRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (pendingSaveRef.current) pendingSaveRef.current();
    };
  }, []);

  const lastPersistedCountFor = useRef('');
  useEffect(() => {
    if (isNew || !template || countStatus !== 'fresh' || count == null) return;
    if (countedFor.current !== criteriaJson || dirty) return;
    if (lastPersistedCountFor.current === criteriaJson && template.md5_count === count) return;
    lastPersistedCountFor.current = criteriaJson;
    if (template.md5_count === count) return;
    saveExisting();
  }, [countStatus, count, criteriaJson, dirty, isNew, template, saveExisting]);

  const createTemplate = async () => {
    if (creating) return;
    const blockers = createBlockers();
    if (blockers.length) {
      setCreateError(blockers.join('\n'));
      return;
    }
    setCreating(true);
    try {
      const created = await segmentTemplatesService.create({
        ...buildPayload(),
        organization_id: parseInt(organizationId, 10),
      });
      toast.success('Template created');
      navigate(`/segments/templates/${created.id}`, { replace: true, state: location.state });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to save template');
    } finally {
      setCreating(false);
    }
  };

  // ---- render -----------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-20 text-sm text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading template…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] space-y-5">
      <div className="space-y-3">
        <Link to={templatesPath} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-[#18181B]">
          <ArrowLeft className="h-4 w-4" /> Templates
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <input
            id="template-name"
            type="text"
            value={name}
            onChange={(e) => setName(sanitizeName(e.target.value))}
            placeholder="Name your template"
            aria-label="Template name"
            className="h-11 min-w-[18rem] flex-1 rounded-lg border border-input bg-surface px-3 font-display text-xl font-bold text-[#18181B] placeholder:font-normal placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
          />
          <div className="flex items-center gap-3">
            {orgIsFixed && organizationName ? (
              <span className="rounded-full bg-[#E8EEF5] px-2.5 py-1 text-xs font-medium text-zinc-600" title="Organization">
                {organizationName}
              </span>
            ) : null}
            <SaveStatus status={saveStatus} isNew={isNew} invalidReason={notSaveableReason} />
            {isNew ? (
              <Button variant="outline" onClick={createTemplate} className="gap-1.5" title="Create the template">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Create template
              </Button>
            ) : null}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="template-description">Description</Label>
          <Textarea
            id="template-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="When to use this template"
            className="min-h-[60px]"
          />
        </div>
      </div>

      {!orgIsFixed && organizations.length > 1 ? (
        <PageHeaderActions>
          <FunctionalSelect
            label="Organization"
            className={HEADER_SELECT_CLASS}
            value={organizationsService.getSelectedOptionValue(sortedOrganizations, organizationId, 'id')}
            onValueChange={(value) => {
              const parsed = organizationsService.parseOrganizationOptionValue(value);
              const nextId = parsed ? parsed.id : value;
              setOrganizationId(nextId);
              if (isNew) organizationsService.setSelectedOrganization(nextId, 'id', sortedOrganizations);
              countedFor.current = '';
              lastSqlFor.current = '';
              setCount(null);
              setCountStatus('idle');
              setGroupCounts(null);
            }}
            placeholder="Select organization"
          >
            {sortedOrganizations.map((org) => (
              <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                {org.name}
              </SelectItem>
            ))}
          </FunctionalSelect>
        </PageHeaderActions>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-4">
          <NlDraftBar
            organizationId={organizationId}
            criteria={criteria}
            hasConditions={Boolean((criteria?.children || []).length)}
            onApply={(tree) => setCriteria(criteriaFromSegment(tree))}
          />
          <div className="rounded-xl border border-hairline bg-surface p-5 shadow-card">
            <CriteriaTree criteria={criteria} onChange={setCriteria} groupCounts={groupCounts} countsStale={countStatus === 'stale'} />
          </div>
        </div>
        <div className="lg:sticky lg:top-0 lg:self-start">
          <SegmentSummaryPane
            criteria={criteria}
            sql={complete ? sql : ''}
            sqlError={sqlError}
            explain={complete ? explain : null}
            organizationName={organizationName}
            count={count}
            countStatus={countStatus}
            countTookMs={countTookMs}
            countError={countError}
            onRunCount={runCount}
            autoCount={autoCount}
            onToggleAutoCount={setAutoCount}
            canRunCount={complete && Boolean(organizationId)}
            analyzeStatus={analyzeStatus}
            analyzeResults={analyzeResults}
            analyzeError={analyzeError}
            onAnalyze={runAnalyze}
            conditionCount={collectConditions(criteria).length}
            samplingActive={samplingActive}
            matchingTotal={matchingTotal}
            contradictions={contradictions}
            onCombineContradictions={combineContradictions}
            targetSize={targetSize}
            onTargetSizeChange={setTargetSize}
            onFitTargetSize={fitTargetSize}
            canFitTargetSize={complete && matchingTotal != null}
            shapeIssues={shapeIssues}
            onHoistSample={hoistStrandedSamples}
            onLoosenLevel={loosenStrandedLevels}
          />
        </div>
      </div>

      <ErrorDialog
        isOpen={Boolean(createError)}
        onClose={() => setCreateError('')}
        title="Cannot create template"
        message={createError}
      />
      <ErrorDialog isOpen={Boolean(loadError)} onClose={() => navigate(templatesPath)} message={loadError} />
    </div>
  );
};

export default TemplateEditor;
